import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IModalInstanceService } from 'angular-ui-bootstrap';

@Component({
    selector: 'cv-agent-states-color-modal',
    templateUrl: './agent-states-color-modal.component.html',
    styleUrls: ['./agent-states-color-modal.component.scss']
})
export class AgentStatesColorModalComponent implements OnInit {

    private static MAX_COLORS_PER_ROW = 6;

    @Input() modalInstance: IModalInstanceService; // Used to pass data through the close() function
    @Input() recentColors = [];
    @Input() currentColor = "#bd2026";
    @Input() defaultColor = "#bd2026";
    @Input() stateDisplayName = "";
    @ViewChild("colorBox") colorBox: ElementRef;

    public showPopover = false;
    public color: any;
    public isSaving = false; // Used to prevent double clicking
    public groupedRecentColors: string[][] = [[]];

    constructor(
        private activeModal: NgbActiveModal,
    ) { }

    /**
     * Loads the data from the modal resolves and sets up the internal variables based on this data.
     */
    ngOnInit(): void {
        if (this.currentColor) {
            // The color picker uses lower-case hex codes.
            this.currentColor = this.currentColor.toLowerCase();
        }

        if (this.defaultColor)
            this.defaultColor = this.defaultColor.toLowerCase();

        if (this.recentColors) {
            // Build the recent color row groups. Each row can only have a certain max number of colors.
            this.recentColors.forEach(c => {
                const outerLength = this.groupedRecentColors.length;
                if (this.groupedRecentColors[outerLength - 1].length >= AgentStatesColorModalComponent.MAX_COLORS_PER_ROW) {
                    this.groupedRecentColors.push([c.toLowerCase()]);
                } else {
                    this.groupedRecentColors[outerLength - 1].push(c.toLowerCase());
                }
            });
        }

        if (this.stateDisplayName)
            this.stateDisplayName = this.stateDisplayName;
    }

    /**
     * Closes the modal without passing anything to the parent directive.
     * Used in the ngClick events on both the Cancel text button and the modal close X button.
     */
    public onCancelClicked() {
        this.activeModal.close();
    }

    /**
     * Used in the front end with the colorPicker Hook to grab which color is selected and
     * fill the input element with the hex color.
     */
    public onColorChange(color) {
        this.colorBox.nativeElement.value = color.value;
    }

    /**
     * function called whenever a character is typed into the textbox. Wont allow spaces or special characters except the # character.
     * Will only accept a string with length of 7 and will auto add the # at the beginning if the user doesnt add it. Doesnt allow # after the first character.
     *
     * @param color of type string
     */
    public typeColor(color: string) {
        const regex = /[^A-F0-9#]/gi;
        // if any character is included in the string but letters numbers and the pound sign remove it and reset the input box
        if (regex.test(color)) {
            color = color.replace(regex, "");
            this.colorBox.nativeElement.value = color;
        }

        // assures there are no pound signs after the potential first one
        if (color.charAt(color.length - 1) === '#' && color.length > 1) {
            color = this.currentColor.slice(0, color.length - 1);
            this.colorBox.nativeElement.value = color;
        }

        // if it is greater than 7 only inlcude 7 characters
        if (color.length > 7) {
            this.colorBox.nativeElement.value = this.currentColor.slice(0,7);
            color = this.currentColor.slice(0,7);
        }
        // if they didn't add a pound sign add it for them.
        if (color.charAt(0) !== "#") {
            color = '#' + color;
        }
        // if it is a full hex code reset the color and input box
        if (color.length === 7) {
            this.currentColor = color;
            this.colorBox.nativeElement.value = color;
        }

    }

    /**
     * Sets the currently selected color to the default color.
     * The default color is either red or orange (when ACW).
     * Used in ngClick on the Use Default Color button.
     */
    public onDefaultColorClicked() {
        this.currentColor = this.defaultColor;
        this.colorBox.nativeElement.value = this.currentColor;

    }

    /**
     * Closes the modal and passes back the selected color.
     * If the color is the default, the string value "default" is passed back.
     * Used in ngClick on the Save button.
     */
    public onSaveClicked() {
        if (this.isSaving)
            return;
        this.isSaving = true;

        let color = this.currentColor;
        if (this.currentColor.toLowerCase() === this.defaultColor)
            color = "default";
        this.activeModal.close(color);
    }

}

interface AgentStatesColorModalResolveValues {
    currentColor: string;
    defaultColor: string;
    recentColors: string[];
    stateDisplayName: string;
}

